import React, { useEffect, useRef } from 'react'

const processSteps = [
  {
    number: '1',
    title: 'Intake',
    description:
      'Kort gesprek over uw situatie, doelen en de partijen die al betrokken zijn.',
  },
  {
    number: '2',
    title: 'Plan',
    description:
      'Concreet advies: welke aanpak, welke partijen en welke stappen nodig zijn.',
  },
  {
    number: '3',
    title: 'Uitvoering & nazorg',
    description:
      'Ik begeleid de uitvoering, houd grip op de voortgang en blijf beschikbaar na oplevering.',
  },
]

const Process: React.FC = () => {
  const stepsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const grid = stepsRef.current
    if (!grid) return

    const cards = Array.from(grid.querySelectorAll<HTMLElement>('.reveal-up'))

    const show = () => cards.forEach((card) => card.classList.add('is-visible'))

    const fallback = setTimeout(show, 1500)

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          show()
          clearTimeout(fallback)
          observer.disconnect()
        }
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' },
    )

    observer.observe(grid)
    return () => {
      clearTimeout(fallback)
      observer.disconnect()
    }
  }, [])

  return (
    <section id="werkwijze" className="py-16 sm:py-20 bg-brand-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-brand-ink leading-tight">
            Werkwijze
          </h2>
          <p className="mt-3 text-brand-muted max-w-xl">
            Geen verrassingen: u weet vooraf wat er gaat gebeuren, wat het kost
            en wie wat doet.
          </p>
        </div>

        {/* Steps */}
        <div
          ref={stepsRef}
          className="grid md:grid-cols-3 gap-px bg-brand-ink/8 rounded-xl overflow-hidden mb-16"
        >
          {processSteps.map((step, index) => (
            <div
              key={step.number}
              className="reveal-up bg-brand-surface px-8 py-8"
              style={{ '--i': index } as React.CSSProperties}
            >
              <span className="block text-brand-accent text-xs font-bold tracking-widest mb-5">
                0{step.number}
              </span>
              <h3 className="text-xl font-bold text-brand-ink mb-2">
                {step.title}
              </h3>
              <p className="text-brand-muted text-sm leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>

        {/* Image / Text */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <img
            src="/img/work-1.jpg"
            alt="LeanC aan het werk op locatie"
            width={6000}
            height={4000}
            className="rounded-xl w-full h-auto"
          />
          <div>
            <h3 className="text-2xl md:text-3xl font-bold text-brand-ink mb-4 leading-tight">
              Persoonlijke aanpak,
              <br className="hidden sm:block" /> professioneel resultaat
            </h3>
            <p className="text-brand-muted leading-relaxed mb-4">
              Geen vaste toolset, geen vendor-voorkeur. Ik leg de techniek in
              gewone taal uit en schakel waar nodig specialisten in voor
              development, hosting of koppelingen.
            </p>
            <p className="text-brand-muted leading-relaxed mb-8">
              Nieuwe website, koppeling of supportvraag: ik pak het op, leg uit
              wat er speelt en zorg dat de juiste mensen erbij betrokken zijn.
            </p>
            <a
              href="#contact"
              className="inline-block bg-brand-accent text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-brand-accent/85 active:scale-[0.97] transition-all duration-200"
            >
              Neem contact op
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Process
